import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { SignaturePadHandle } from '../components/SignatureCanvas'
import { DocumentSignPanel } from '../features/legal/DocumentSignPanel'
import type { DocumentStatement } from '../features/legal/DocumentSignPanel'
import { EmailCodeForm } from '../features/account/EmailCodeForm'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Button, Card, CardBody, Input, Spinner, DateField } from '@structural-climbing/ui'
import { isValidPhone } from '@structural-climbing/validation'
import { isMinor } from '../features/legal/minor'
import { EMPTY_GUARDIAN, GuardianFields, guardianComplete } from '../features/legal/GuardianFields'

import { useGymTheme } from '../features/gym/useGymTheme'

interface GymInfo {
  id: string
  name: string
  slug: string
  description: string | null
  image_url: string | null
  primary_color: string | null
  secondary_color: string | null
  // False while the gym is being set up. The server refuses a request either
  // way; this only spares the visitor a form they cannot submit.
  accepting_members: boolean
  required_documents: { id: string; title: string; statements?: DocumentStatement[] }[]
  /** What a parent or guardian signs for someone under 18. Empty: ask at the desk. */
  minor_documents?: { id: string; title: string; statements?: DocumentStatement[] }[]
  /** Linked from the signing screen. Absent when the gym has none. */
  privacy_notice?: { id: string; version: number; path: string } | null
  /** Whether a signer must prove their email address before signing here. */
  require_verified_email?: boolean
  /**
   * The same for a minor's parent or guardian. It holds even where the gym
   * has no children's document and nothing is signed online.
   */
  minor_require_verified_email?: boolean
}

type PageState =
  | 'loading'
  | 'not_found'
  | 'coming_soon'
  | 'form'
  | 'already_requested'
  | 'verify_email'
  | 'signing'
  | 'submitting'

type SignStep = 'view' | 'sign'

// `new Date('2008-08-23')` is parsed as UTC midnight, which renders as the day
// before in any negative-offset zone. Build the date from its parts instead.
function formatBirthDate(value: string): string {
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return value
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

// One labelled fact in the summary. A bare "23 August 2008" beside an email
// gives no clue which is which, and a phone hidden because it is empty reads as
// though the field does not exist.
function DetailRow({
  label,
  value,
  emptyLabel,
}: {
  label: string
  value: string
  emptyLabel: string
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-xs">
      <dt className="shrink-0 text-foreground/50">{label}</dt>
      <dd
        className={`min-w-0 truncate text-right ${value ? 'text-foreground/80' : 'text-foreground/40'}`}
      >
        {value || emptyLabel}
      </dd>
    </div>
  )
}

function GymHeader({ gym, sub }: { gym: GymInfo; sub?: string }) {
  return (
    <div className="mb-6 text-center">
      {gym.image_url && (
        <img
          src={gym.image_url}
          alt={gym.name}
          className="mx-auto mb-3 h-16 w-16 rounded-xl object-cover"
        />
      )}
      <h1 className="text-2xl font-semibold">{gym.name}</h1>
      {gym.description && <p className="mt-1 text-sm text-foreground/60">{gym.description}</p>}
      {sub && <p className="mt-0.5 text-xs text-foreground/40">{sub}</p>}
    </div>
  )
}

export function JoinPage() {
  const { gymSlug } = useParams<{ gymSlug: string }>()
  const navigate = useNavigate()
  const {
    isLoading: authLoading,
    isAuthenticated,
    user,
    loginWithRedirect,
    getAccessTokenSilently,
  } = useAuth()
  const { t, i18n } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [gym, setGym] = useState<GymInfo | null>(null)

  // These pages sit outside GymLayout, so nothing else paints them. A gym's
  // join page is the first thing a new member ever sees of it, and it should
  // arrive wearing that gym's colours rather than Structural's defaults.
  useGymTheme(gym?.primary_color ?? null, gym?.secondary_color ?? null)
  const [pageState, setPageState] = useState<PageState>('loading')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [existingStatus, setExistingStatus] = useState<'pending' | 'approved' | null>(null)

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  // Empty until the profile or the member fills it. It used to start at "18
  // years ago today", which recorded anyone who skipped it as exactly 18.
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [guardian, setGuardian] = useState(EMPTY_GUARDIAN)
  // The code the guardian confirmed, and the address it was confirmed for.
  const [guardianCode, setGuardianCode] = useState<{ email: string; id: string } | null>(null)
  const [phone, setPhone] = useState('')
  // Whether the profile already answered name and date of birth, and whether
  // the member has asked to change them here.
  const [detailsFromProfile, setDetailsFromProfile] = useState(false)
  const [editingDetails, setEditingDetails] = useState(false)

  const [sigIndex, setSigIndex] = useState(0)
  const [signStep, setSignStep] = useState<SignStep>('view')
  const [signatures, setSignatures] = useState<Map<string, Blob>>(new Map())
  // Per document: the statements the signer ticked. Starts empty for every
  // document, so a box is only ever ticked by the person signing.
  const [tickedByDoc, setTickedByDoc] = useState<Map<string, string[]>>(new Map())
  // The account's address and whether it has been proved, from the profile.
  const [accountEmail, setAccountEmail] = useState<string | null>(null)
  const [emailVerified, setEmailVerified] = useState(false)
  const [docUrl, setDocUrl] = useState<string | null>(null)
  const [docIsPdf, setDocIsPdf] = useState(false)
  const [docLoading, setDocLoading] = useState(false)
  const [canvasEmpty, setCanvasEmpty] = useState(true)

  const sigRef = useRef<SignaturePadHandle | null>(null)
  const objectUrlRef = useRef<string | null>(null)

  useEffect(() => {
    if (!gymSlug) {
      setPageState('not_found')
      return
    }
    fetch(`${apiUrl}/join-info/${encodeURIComponent(gymSlug)}`)
      .then(async (res) => {
        if (!res.ok) {
          setPageState('not_found')
          return
        }
        const data = (await res.json()) as GymInfo
        setGym(data)
        setPageState(data.accepting_members ? 'form' : 'coming_soon')
      })
      .catch(() => setPageState('not_found'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gymSlug])

  // Someone under 18 does not sign. A parent or guardian does, on the gym's
  // children's document, and proves an address of their own.
  const minor = !!dateOfBirth && isMinor(dateOfBirth)
  const signDocs = (minor ? gym?.minor_documents : gym?.required_documents) ?? []
  const minorName = `${firstName.trim()} ${lastName.trim()}`.trim()
  const guardianEmail = guardian.email.trim().toLowerCase()

  useEffect(() => {
    if (!isAuthenticated) return
    async function prefill() {
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${apiUrl}/my-profile`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) return
        const data = (await res.json()) as {
          firstName: string | null
          lastName: string | null
          phone: string | null
          dateOfBirth: string | null
          email?: string | null
          emailVerified?: boolean
        }
        setAccountEmail(data.email ?? null)
        setEmailVerified(data.emailVerified === true)
        if (data.firstName) setFirstName(data.firstName)
        else if (user?.given_name) setFirstName(user.given_name)
        if (data.lastName) setLastName(data.lastName)
        else if (user?.family_name) setLastName(user.family_name)
        if (data.phone) setPhone(data.phone)
        if (data.dateOfBirth) setDateOfBirth(data.dateOfBirth)
        // The summary stands in for the form only when it has nothing left to
        // ask. A profile from before the phone was required opens the form.
        if (data.firstName && data.lastName && data.dateOfBirth && isValidPhone(data.phone ?? ''))
          setDetailsFromProfile(true)
      } catch {
        // silently ignore — user fills manually
      }
    }
    void prefill()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  useEffect(() => {
    if (pageState !== 'form' || authLoading || !isAuthenticated || !gym) return
    const currentGym = gym
    let cancelled = false
    async function check() {
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${apiUrl}/my-join-request`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (cancelled || !res.ok) return
        const data = (await res.json()) as {
          requests?: { gym_id: string; status: 'pending' | 'approved' }[]
          request: { gym_id: string; status: 'pending' | 'approved' } | null
        }
        // Look through every request, not only the newest: a request at another
        // gym used to mask the one for this gym, letting the member sign the
        // documents before the server refused the submission.
        const all = data.requests ?? (data.request ? [data.request] : [])
        const mine = all.find((r) => r.gym_id === currentGym.id)
        if (!cancelled && mine) {
          setExistingStatus(mine.status)
          setPageState('already_requested')
        }
      } catch {
        // silently ignore — user sees the form
      }
    }
    void check()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState, authLoading, isAuthenticated, gym?.id])

  useEffect(() => {
    if (pageState !== 'signing') return
    const currentDoc = signDocs[sigIndex]
    if (!currentDoc) return

    let cancelled = false
    setDocLoading(true)
    setDocUrl(null)
    setDocIsPdf(false)
    setSignStep('view')
    setCanvasEmpty(true)

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = null
    }

    fetch(`${apiUrl}/public-document/${encodeURIComponent(gymSlug ?? '')}/${currentDoc.id}`)
      .then(async (res) => {
        if (cancelled || !res.ok) return
        const blob = await res.blob()
        if (cancelled) return
        const url = URL.createObjectURL(blob)
        objectUrlRef.current = url
        setDocIsPdf(blob.type === 'application/pdf')
        setDocUrl(url)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setDocLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState, sigIndex])

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
    }
  }, [])

  // Where the gym asks for it, the address is proved before the first
  // document, so nobody draws a signature the Worker is about to refuse.
  function startSigning() {
    if (minor) {
      // The guardian is the signer, so it is their address that is proved.
      const codeRequired = gym?.minor_require_verified_email ?? gym?.require_verified_email
      if (codeRequired && guardianCode?.email !== guardianEmail) {
        setErrorMsg(null)
        setPageState('verify_email')
        return
      }
      beginSigning()
      return
    }
    if (gym?.require_verified_email && !emailVerified && accountEmail) {
      setErrorMsg(null)
      setPageState('verify_email')
      return
    }
    beginSigning()
  }

  // With nothing to sign online (a minor at a gym with no children's
  // document), confirming the address is the last step: the request goes out.
  function beginSigning(confirmedCodeId?: string) {
    if (signDocs.length === 0) {
      void handleSubmit(new Map(), confirmedCodeId)
      return
    }
    setSigIndex(0)
    setSignatures(new Map())
    setTickedByDoc(new Map())
    setPageState('signing')
  }

  function captureSignature(): Blob | null {
    if (!sigRef.current || sigRef.current.isEmpty()) return null
    const dataUrl = sigRef.current.toDataURL('image/png')
    const byteString = atob(dataUrl.split(',')[1])
    const ab = new ArrayBuffer(byteString.length)
    const ia = new Uint8Array(ab)
    for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i)
    return new Blob([ab], { type: 'image/png' })
  }

  function handleAdvance() {
    const blob = captureSignature()
    if (!blob) return

    const docs = signDocs
    const currentDoc = docs[sigIndex]
    const newSigs = new Map(signatures)
    newSigs.set(currentDoc.id, blob)
    setSignatures(newSigs)

    sigRef.current?.clear()

    const next = sigIndex + 1
    if (next < docs.length) {
      setSigIndex(next)
    } else {
      void handleSubmit(newSigs)
    }
  }

  async function handleSubmit(sigs?: Map<string, Blob>, confirmedCodeId?: string) {
    if (!gym) return
    setPageState('submitting')
    setErrorMsg(null)

    try {
      const token = await getAccessTokenSilently()
      const finalSigs = sigs ?? signatures
      const formData = new FormData()
      formData.append('gym_slug', gym.slug)
      formData.append('user_first_name', firstName.trim())
      formData.append('user_last_name', lastName.trim())
      formData.append('user_date_of_birth', dateOfBirth)
      formData.append('user_email', user?.email ?? '')
      if (phone.trim()) formData.append('user_phone', phone.trim())
      for (const [docId, blob] of finalSigs) {
        formData.append(`sig_${docId}`, blob, `${docId}.png`)
      }
      // Sent for every document, even with nothing ticked: it is how the
      // Worker knows this build showed the statements at all.
      for (const doc of signDocs) {
        formData.append(`ticked_${doc.id}`, JSON.stringify(tickedByDoc.get(doc.id) ?? []))
      }
      if (minor) {
        formData.append('guardian_first_name', guardian.firstName.trim())
        formData.append('guardian_last_name', guardian.lastName.trim())
        formData.append('guardian_email', guardianEmail)
        formData.append('guardian_declaration', String(guardian.declared))
        formData.append('guardian_phone', guardian.phone.trim())
        // Passed in when the code was confirmed a moment ago and the state has
        // not caught up with it yet.
        const codeId =
          confirmedCodeId ?? (guardianCode?.email === guardianEmail ? guardianCode.id : null)
        if (codeId) formData.append('verification_id', codeId)
      }
      formData.append('language', i18n.language.slice(0, 2))

      const res = await fetch(`${apiUrl}/join-request`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })

      const data = (await res.json()) as { error?: string; code?: string }
      if (!res.ok) {
        // The gym turned the requirement on while this form was open.
        if (data.code === 'EMAIL_NOT_VERIFIED' && (minor || accountEmail)) {
          if (minor) setGuardianCode(null)
          else setEmailVerified(false)
          setPageState('verify_email')
          return
        }
        setErrorMsg(data.error ?? t('join.errorDefault'))
        // Sent to the desk, or told the form is out of date: back to the form,
        // where the message can be read, not to a signing screen.
        const backToForm =
          data.code === 'GUARDIAN_PHONE_REQUIRED' ||
          data.code === 'GUARDIAN_REQUIRED' ||
          data.code === 'GUARDIAN_EMAIL_SAME'
        setPageState(signDocs.length > 0 && !backToForm ? 'signing' : 'form')
        return
      }

      void navigate('/')
    } catch {
      setErrorMsg(t('join.networkError'))
      setPageState(signDocs.length > 0 ? 'signing' : 'form')
    }
  }

  if (pageState === 'loading' || pageState === 'submitting') {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <Spinner size="lg" color="primary" />
      </div>
    )
  }

  if (pageState === 'not_found') {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
        <p className="text-lg font-semibold">{t('join.gymNotFound')}</p>
        <p className="text-sm text-foreground/60">{t('join.checkLink')}</p>
      </div>
    )
  }

  if (pageState === 'coming_soon') {
    return (
      <div className="mx-auto w-full max-w-md p-6">
        <GymHeader gym={gym!} />
        <Card>
          <CardBody className="flex flex-col items-center gap-2 py-8 text-center">
            <p className="text-base font-semibold">{t('join.comingSoonTitle')}</p>
            <p className="text-sm text-foreground/60">{t('join.comingSoonBody')}</p>
          </CardBody>
        </Card>
      </div>
    )
  }

  // Wait for Auth0 to finish restoring session before deciding if auth gate is needed
  if (pageState === 'form' && authLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <Spinner size="lg" color="primary" />
      </div>
    )
  }

  // Auth gate — shown when gym info is loaded but user is not authenticated
  if (pageState === 'form' && !isAuthenticated) {
    return (
      <div className="mx-auto w-full max-w-md p-6">
        <GymHeader gym={gym!} />
        <Card>
          <CardBody className="flex flex-col gap-4 py-5">
            <div>
              <p className="text-base font-semibold">{t('join.signInTitle')}</p>
              <p className="mt-0.5 text-xs text-foreground/50">
                {t('join.signInSubtitle', { gymName: gym!.name })}
              </p>
            </div>
            <Button
              color="primary"
              className="w-full"
              onClick={() => {
                const returnTo = `/join/${gymSlug ?? ''}`
                sessionStorage.setItem('auth_return_to', returnTo)
                void loginWithRedirect({
                  appState: { returnTo },
                  authorizationParams: { screen_hint: 'signup' },
                })
              }}
            >
              {t('join.signInButton')}
            </Button>
          </CardBody>
        </Card>
      </div>
    )
  }

  if (pageState === 'already_requested') {
    const isApproved = existingStatus === 'approved'
    return (
      <div className="mx-auto w-full max-w-md p-6">
        <GymHeader gym={gym!} />
        <Card>
          <CardBody className="flex flex-col items-center gap-4 py-8 text-center">
            <div className="text-4xl">{isApproved ? '✓' : '⏳'}</div>
            <p className="text-base font-semibold">
              {isApproved ? t('join.requestApproved') : t('join.alreadySubmitted')}
            </p>
            <p className="text-sm text-foreground/60">
              {isApproved ? t('join.approvedMessage') : t('join.pendingMessage')}
            </p>
            <Button color="primary" className="w-full" onClick={() => void navigate('/')}>
              {t('join.goToDashboard')}
            </Button>
          </CardBody>
        </Card>
      </div>
    )
  }

  if (pageState === 'verify_email' && gym && minor) {
    return (
      <div className="mx-auto w-full max-w-md p-6">
        <GymHeader gym={gym} />
        <Card>
          <CardBody className="flex flex-col gap-4 p-6">
            <h2 className="text-lg font-semibold">{t('emailCode.beforeSigningTitle')}</h2>
            <p className="text-sm text-foreground">{t('legal.guardianEmailHint')}</p>
            <EmailCodeForm
              email={guardianEmail}
              guest={{ gymSlug: gym.slug, email: guardianEmail }}
              onVerified={(result) => {
                setGuardianCode({ email: guardianEmail, id: result.verificationId })
                // Already signed everything and only the confirmation had
                // lapsed: send what was drawn, do not make them sign again.
                if (signatures.size > 0 && signatures.size === signDocs.length)
                  void handleSubmit(signatures, result.verificationId)
                else beginSigning(result.verificationId)
              }}
            />
            <Button variant="light" size="sm" onClick={() => setPageState('form')}>
              {t('emailCode.changeEmail')}
            </Button>
          </CardBody>
        </Card>
      </div>
    )
  }

  if (pageState === 'verify_email' && gym && accountEmail) {
    return (
      <div className="mx-auto w-full max-w-md p-6">
        <GymHeader gym={gym} />
        <Card>
          <CardBody className="flex flex-col gap-4 p-6">
            <h2 className="text-lg font-semibold">{t('emailCode.beforeSigningTitle')}</h2>
            <p className="text-sm text-foreground">{t('emailCode.beforeSigningBody')}</p>
            <EmailCodeForm
              email={accountEmail}
              onVerified={() => {
                setEmailVerified(true)
                // Already signed everything and only the address was missing:
                // send what was drawn, do not make them sign again.
                if (signatures.size > 0 && signatures.size === signDocs.length)
                  void handleSubmit(signatures)
                else beginSigning()
              }}
            />
          </CardBody>
        </Card>
      </div>
    )
  }

  const docs = signDocs
  const currentDoc = docs[sigIndex]

  // Signing phase
  if (pageState === 'signing' && currentDoc) {
    const total = docs.length
    const isLastDoc = sigIndex === total - 1

    return (
      <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col gap-4 p-4 sm:p-6">
        <div className="flex shrink-0 items-center gap-3">
          {gym?.image_url && (
            <img src={gym.image_url} alt={gym.name} className="h-10 w-10 rounded-lg object-cover" />
          )}
          <div>
            <p className="text-sm font-semibold">{gym?.name}</p>
            <p className="text-xs text-foreground/40">
              {t('join.documentOf', { current: sigIndex + 1, total })}
            </p>
          </div>
        </div>

        {/* min-h-0 flex-1 all the way down, so the document gives up height
            before the sign button does. RootLayout is h-dvh and this chain
            reaches it, which means the card fits whatever the nav bars leave. */}
        <div className="flex min-h-0 flex-1 flex-col gap-3 rounded-2xl border border-divider bg-content1 p-4 shadow-sm sm:p-6">
          <h2 className="shrink-0 text-base font-semibold">{currentDoc.title}</h2>
          {minor && (
            <p className="shrink-0 text-sm font-medium text-primary">
              {t('legal.signingAsGuardian', { name: minorName })}
            </p>
          )}

          <DocumentSignPanel
            step={signStep}
            docTitle={currentDoc.title}
            docUrl={docUrl}
            docIsPdf={docIsPdf}
            docLoading={docLoading}
            error={errorMsg}
            signLabel={isLastDoc ? t('join.signAndSubmit') : t('join.signAndContinue')}
            canvasEmpty={canvasEmpty}
            statements={currentDoc.statements}
            ticked={tickedByDoc.get(currentDoc.id) ?? []}
            onTickedChange={(ids) => setTickedByDoc(new Map(tickedByDoc).set(currentDoc.id, ids))}
            privacyNoticeUrl={gym?.privacy_notice ? `${apiUrl}${gym.privacy_notice.path}` : null}
            sigRef={sigRef}
            onCanvasEnd={() => setCanvasEmpty(sigRef.current?.isEmpty() ?? true)}
            onClear={() => {
              sigRef.current?.clear()
              setCanvasEmpty(true)
            }}
            onProceed={() => setSignStep('sign')}
            onBack={() => setSignStep('view')}
            onSign={handleAdvance}
          />
        </div>
      </div>
    )
  }

  // Form phase (authenticated)
  const hasRequiredDocs = docs.length > 0
  // A gym with no children's document still takes a minor: the parent is
  // named and proves an address, nothing is signed online, and the gym app
  // tells the desk to have the agreement signed on paper.
  const minorOnPaper = minor && docs.length === 0
  const canProceed =
    firstName.trim() &&
    lastName.trim() &&
    dateOfBirth &&
    user?.email &&
    isValidPhone(phone) &&
    (!minor ||
      guardianComplete(guardian, { needsEmail: true, memberEmail: user.email, needsPhone: true }))
  const phoneError = phone.trim() && !isValidPhone(phone) ? t('join.phoneInvalidError') : ''
  // Nothing to ask when the profile already knows: re-typing a name you have
  // already given is how the request and the profile drift apart.
  const showDetailsSummary = detailsFromProfile && !editingDetails

  return (
    <div className="mx-auto w-full max-w-md p-6">
      <GymHeader gym={gym!} />

      <Card>
        <CardBody className="flex flex-col gap-4 py-5">
          <div>
            <p className="text-base font-semibold">{t('join.requestTitle')}</p>
            <p className="mt-0.5 text-xs text-foreground/50">
              {hasRequiredDocs ? t('join.requestWithDocs') : t('join.requestWithoutDocs')}
            </p>
          </div>

          {showDetailsSummary ? (
            <div className="rounded-medium bg-content2 px-3 py-3">
              <p className="text-xs text-foreground/50">{t('join.joiningAsLabel')}</p>
              <p className="mt-0.5 text-sm font-semibold">
                {firstName} {lastName}
              </p>
              <dl className="mt-2.5 flex flex-col gap-1.5">
                <DetailRow
                  label={t('join.dateOfBirthLabel')}
                  value={formatBirthDate(dateOfBirth)}
                  emptyLabel={t('join.notAdded')}
                />
                <DetailRow
                  label={t('join.emailLabel')}
                  value={user?.email ?? ''}
                  emptyLabel={t('join.notAdded')}
                />
                <DetailRow
                  label={t('profile.phoneLabel')}
                  value={phone}
                  emptyLabel={t('join.notAdded')}
                />
              </dl>
              <p className="mt-3 text-xs text-foreground/40">{t('join.detailsFromProfile')}</p>
              <Button
                size="sm"
                variant="flat"
                className="mt-2"
                onPress={() => setEditingDetails(true)}
              >
                {t('join.changeDetails')}
              </Button>
            </div>
          ) : (
            <>
              <Input
                label={t('join.firstNameLabel')}
                placeholder={t('join.firstNamePlaceholder')}
                value={firstName}
                onValueChange={setFirstName}
                isRequired
              />
              <Input
                label={t('join.lastNameLabel')}
                placeholder={t('join.lastNamePlaceholder')}
                value={lastName}
                onValueChange={setLastName}
                isRequired
              />
              <DateField
                label={t('join.dateOfBirthLabel')}
                value={dateOfBirth}
                onValueChange={setDateOfBirth}
                isRequired
                max={new Date().toISOString().slice(0, 10)}
              />
              <Input
                label={t('join.emailLabel')}
                type="email"
                value={user?.email ?? ''}
                isRequired
                isReadOnly
              />
              <Input
                label={t('join.phoneLabel')}
                type="tel"
                placeholder={t('join.phonePlaceholder')}
                value={phone}
                onValueChange={setPhone}
                isRequired
                isInvalid={!!phoneError}
                errorMessage={phoneError || undefined}
              />
            </>
          )}

          {minor && (
            <GuardianFields
              value={guardian}
              onChange={setGuardian}
              minorName={minorName}
              gymName={gym!.name}
              withEmail
              withPhone
              memberEmail={user?.email}
            />
          )}
          {minorOnPaper && (
            <p role="note" className="text-sm text-foreground">
              {t('legal.minorPaperNotice')}
            </p>
          )}

          {hasRequiredDocs && (
            <div className="rounded-lg border border-divider bg-content2/50 p-3">
              <p className="mb-1 text-xs font-medium text-foreground/70">{t('join.mustSign')}</p>
              <ul className="flex flex-col gap-0.5">
                {docs.map((doc) => (
                  <li key={doc.id} className="text-xs text-foreground/60">
                    · {doc.title}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {errorMsg && <p className="text-sm text-danger">{errorMsg}</p>}

          <Button
            color="primary"
            isDisabled={!canProceed || !!phoneError}
            onClick={() => (hasRequiredDocs || minor ? startSigning() : void handleSubmit())}
            className="w-full"
          >
            {hasRequiredDocs ? t('join.nextSignDocs') : t('join.submitButton')}
          </Button>
        </CardBody>
      </Card>
    </div>
  )
}
